import { AdvancedFilterConditionOperator, AdvancedFilterDto, AdvancedFilterLogicalOperator, BasicFilterDto, BasicFilterOperator, DynamicFilterDto, DynamicFilterValue, ExprFilterDto, FilterDto, FilterType, RelativeDateFilterDto, RelativeDateOrTimeOperator, RelativeDateTimeFilterUnit, RelativeTimeFilterDto, SchemaFieldTargetDto, SchemaTargetDto, TopNFilterDto, TopNFilterOperator } from 'src/app/services/api.services';
import { deepClone } from 'src/app/shared/clone/cloner-service';
import { AdvancedFilterConditionOperators, DataTypeMap, isAdvancedFilter, isBasicFilter, isDynamicFilter, isExprFilter, isRelativeDateFilter, isRelativeTimeFilter, isTopNFilter, OperatorValueRequirement } from 'src/app/shared/compatibility/filter.models';
import { isNullOrUndefined } from 'src/app/shared/utils';
import { InsightsQuery } from 'src/insightsQuery';

export enum PredicateType {
  Basic = FilterType.Basic,
  Advanced = FilterType.Advanced,
  RelativeDate = FilterType.RelativeDate,
  RelativeTime = FilterType.RelativeTime,
  Dynamic = FilterType.Dynamic,
  TopN = FilterType.TopN,
  Expr = FilterType.Expr,
}

export type Predicate<T> =
  BasicPredicate<T> |
  AdvancedPredicate<T> |
  RelativeDatePredicate<T> |
  RelativeTimePredicate<T> |
  DynamicPredicate<T> |
  TopNPredicate<T> |
  ExprPredicate<T>;

export interface BasicPredicate<T> {
  readonly type: PredicateType.Basic;
  readonly target?: T;
  readonly operator: Predicate.BasicOperator;
  readonly values: ReadonlyArray<any>;
}

export interface AdvancedPredicate<T> {
  readonly type: PredicateType.Advanced;
  readonly target?: T;
  readonly logicalOperator: Predicate.LogicalOperator;
  readonly conditions: ReadonlyArray<Predicate.Condition>;
}

export interface RelativeDatePredicate<T> {
  readonly type: PredicateType.RelativeDate;
  readonly target?: T;
  readonly operator: RelativeDateOrTimeOperator;

  readonly firstUnitCount: number;
  readonly firstUnitType: RelativeDateTimeFilterUnit;

  readonly secondUnitCount?: number;
  readonly secondUnitType?: RelativeDateTimeFilterUnit;

  readonly includeToday?: boolean;
}

export interface RelativeTimePredicate<T> {
  readonly type: PredicateType.RelativeTime;
  readonly target?: T;
  readonly operator: RelativeDateOrTimeOperator;

  readonly firstUnitCount: number;
  readonly firstUnitType: RelativeDateTimeFilterUnit;

  readonly secondUnitCount?: number;
  readonly secondUnitType?: RelativeDateTimeFilterUnit;
}

export interface DynamicPredicate<T> {
  readonly type: PredicateType.Dynamic;
  readonly target?: T;
  readonly kind: DynamicFilterValue;
}

export interface TopNPredicate<T> {
  readonly type: PredicateType.TopN;
  readonly target?: T;
  readonly operator: Predicate.TopNOperator;
  readonly maxCount: number;
}

export interface ExprPredicate<T> {
  readonly type: PredicateType.Expr;
  readonly target?: T;
  readonly expr?: string;
}

export namespace Predicate {
  export enum BasicOperator {
    All = BasicFilterOperator.All,
    OneOf = BasicFilterOperator.In,
    NotOneOf = BasicFilterOperator.NotIn,
  }

  export enum LogicalOperator {
    And = AdvancedFilterLogicalOperator.And,
    Or = AdvancedFilterLogicalOperator.Or,
  }

  export interface Condition {
    readonly type: ConditionType;
    readonly value?: any;
  }

  export enum ConditionType {
    Is = AdvancedFilterConditionOperator.Is,
    IsNot = AdvancedFilterConditionOperator.IsNot,
    IsBlank = AdvancedFilterConditionOperator.IsBlank,
    IsNotBlank = AdvancedFilterConditionOperator.IsNotBlank,
    GreaterThan = AdvancedFilterConditionOperator.GreaterThan,
    GreaterThanOrEqual = AdvancedFilterConditionOperator.GreaterThanOrEqual,
    LessThan = AdvancedFilterConditionOperator.LessThan,
    LessThanOrEqual = AdvancedFilterConditionOperator.LessThanOrEqual,
    Contains = AdvancedFilterConditionOperator.Contains,
    DoesNotContain = AdvancedFilterConditionOperator.DoesNotContain,
    StartsWith = AdvancedFilterConditionOperator.StartsWith,
    DoesNotStartWith = AdvancedFilterConditionOperator.DoesNotStartWith,
  }

  export enum TopNOperator {
    Top = TopNFilterOperator.Top,
    Bottom = TopNFilterOperator.Bottom,
  }

  export function aggregationsOnly(type: PredicateType) {
    return type === PredicateType.TopN;
  }

  export function normalize<T>(predicate: Predicate<T>) {
    predicate = deepClone(predicate);

    if (predicate.type == PredicateType.Basic) {
      if (predicate.operator != BasicOperator.All && !predicate.values?.length) {
        // No values in one of or not one of - becomes all
        predicate = {
          ...predicate,
          operator: BasicOperator.All
        };
      }
    }
    else if (predicate.type == PredicateType.Advanced) {
      // Remove invalid conditions
      predicate = {
        ...predicate,
        conditions: predicate.conditions?.filter(c => {
          if (AdvancedFilterConditionOperators.find(x => x.id == <any>c.type)?.valueRequirement == OperatorValueRequirement.Required) {
            if (isNullOrUndefined(c.value) || (typeof c.value === 'string' && c.value === '')) {
              return false;
            }
          }

          return true;
        })
      };
    }
    else if (predicate.type == PredicateType.RelativeDate || predicate.type == PredicateType.RelativeTime) {
      // Remove 2nd unit condition, if either property is missing
      if (isNullOrUndefined(predicate.secondUnitType) || isNullOrUndefined(predicate.secondUnitCount) || isNaN(predicate.secondUnitCount)) {
        predicate = {
          ...predicate,
          secondUnitType: null
        };
      }
    }

    return predicate;
  }

  export function isComplete<T>(predicate: Predicate<T>) {
    if (predicate.type == PredicateType.Basic) {
      if (!predicate.target)
        return false;

      if (predicate.operator != BasicOperator.All) {
        if (!predicate.values?.length)
          return false;
      }
    }

    if (predicate.type == PredicateType.Advanced) {
      if (!predicate.target)
        return false;

      if (!predicate.conditions?.length)
        return false;
    }

    if (predicate.type == PredicateType.RelativeDate || predicate.type == PredicateType.RelativeTime) {
      if (!predicate.target)
        return false;

      if (isNullOrUndefined(predicate.firstUnitType) || isNullOrUndefined(predicate.firstUnitCount))
        return false;

      if (!isNullOrUndefined(predicate.secondUnitType) && isNullOrUndefined(predicate.secondUnitCount))
        return false;
    }

    if (predicate.type == PredicateType.Dynamic) {
      if (!predicate.target)
        return false;

      if (isNullOrUndefined(predicate.kind))
        return false;
    }

    if (predicate.type == PredicateType.TopN) {
      if (!predicate.target)
        return false;

      if (isNullOrUndefined(predicate.operator) || isNullOrUndefined(predicate.maxCount))
        return false;
    }

    if (predicate.type == PredicateType.Expr) {
      let expr = predicate.expr?.trim();
      if (!expr)
        return false;

      try {
        InsightsQuery.parse(expr, null, true);
      }
      catch {
        return false;
      }
    }

    return true;
  }

  export function fromFilterDto<T>(
    dto: FilterDto,
    fieldResolver: (target: SchemaFieldTargetDto) => T): Predicate<T> {
    let predicate: Predicate<T>;

    if (isAdvancedFilter(dto)) {
      predicate = {
        type: PredicateType.Advanced,
        target: fieldResolver(dto.target),
        logicalOperator: <any>dto.logicalOperator,
        conditions: dto.conditions?.map(c => (<Condition>{
          type: <any>c.operator,
          value: c.value,
        })) ?? []
      };
    }
    else if (isBasicFilter(dto)) {
      predicate = {
        type: PredicateType.Basic,
        target: fieldResolver(dto.target),
        operator: <any>dto.operator,
        values: dto.values
      };
    }
    else if (isDynamicFilter(dto)) {
      predicate = {
        type: PredicateType.Dynamic,
        target: fieldResolver(dto.target),
        kind: dto.value
      };
    }
    else if (isRelativeDateFilter(dto)) {
      predicate = {
        type: PredicateType.RelativeDate,
        target: fieldResolver(dto.target),
        operator: dto.operator,
        firstUnitCount: dto.timeUnitsCount,
        firstUnitType: dto.timeUnitType,
        secondUnitCount: dto.timeUnitsCount2,
        secondUnitType: dto.timeUnitType2,
        includeToday: dto.includeToday
      };
    }
    else if (isRelativeTimeFilter(dto)) {
      predicate = {
        type: PredicateType.RelativeTime,
        target: fieldResolver(dto.target),
        operator: dto.operator,
        firstUnitCount: dto.timeUnitsCount,
        firstUnitType: dto.timeUnitType,
        secondUnitCount: dto.timeUnitsCount2,
        secondUnitType: dto.timeUnitType2,
      };
    }
    else if (isTopNFilter(dto)) {
      predicate = {
        type: PredicateType.TopN,
        target: fieldResolver(dto.orderBy),
        operator: <any>dto.operator,
        maxCount: dto.itemCount
      };
    }
    else if (isExprFilter(dto)) {
      // TODO: expr filter
    }

    return predicate;
  }
}
